import { api } from "./api.js";
import { getCurrentUser } from "./auth.js";

/**
 * The badge on Cohorts in the navbar: how many of your cohorts have chat
 * messages you haven't read, as Messenger counts conversations rather than
 * messages (GET /chats/unread).
 *
 * Asked for on signing in, every POLL_MS while the tab is in front (as the
 * bell is), and when the tab comes back into view. The Cohorts page, which
 * has the live list, sets it as messages arrive and are read.
 *
 * Not shown on the Cohorts page itself, whose list has each cohort's own
 * count of unread messages; it's still kept up to date there, for leaving.
 */
const POLL_MS = 60_000;

let count = 0;
let pollHandle = null;
let bound = false;

/** Whether the address is the Cohorts page's: #/cohorts, a cohort's chat, or
 *  their older /community spellings. Judged by the address, not the page on
 *  screen: the navbar is repainted just before the next page replaces it. */
export function onCohortsPage(hash = location.hash) {
  return /^#\/(cohorts|communities)(\?|$)/.test(hash) || /^#\/(cohort|community)\/[^/]+\/chat(\?|$)/.test(hash);
}

/** Paints the badge (app.js re-renders the navbar, so after that too). */
export function paintChatBadge() {
  const badge = document.getElementById("nav-cohorts-unread");
  if (!badge) return;
  const shown = count > 0 && !onCohortsPage();
  badge.textContent = shown ? String(count) : "";
  badge.classList.toggle("d-none", !shown);
  badge.setAttribute("aria-label", `${count} ${count === 1 ? "cohort" : "cohorts"} with unread messages`);
}

export function setChatBadge(value) {
  count = Math.max(0, value || 0);
  paintChatBadge();
}

export async function refreshChatBadge() {
  if (getCurrentUser() == null) {
    setChatBadge(0);
    return;
  }
  try {
    const { unread_chats: unread } = await api.get("/chats/unread");
    setChatBadge(unread ?? 0);
  } catch {
    // As the bell's: a failed poll leaves the badge as it was, and the next
    // one in a minute tries again.
  }
}

function startPolling() {
  stopPolling();
  if (document.visibilityState !== "visible" || getCurrentUser() == null) return;
  pollHandle = setInterval(refreshChatBadge, POLL_MS);
}

function stopPolling() {
  if (pollHandle != null) clearInterval(pollHandle);
  pollHandle = null;
}

/** Called on every auth change: starts or stops the badge. */
export function refreshChatBadgeVisibility() {
  refreshChatBadge();
  startPolling();
}

export function initChatBadge() {
  if (bound) return;
  bound = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      refreshChatBadge();
      startPolling();
    } else {
      stopPolling();
    }
  });
}
